import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { X, Search } from "lucide-react";

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [sizes, setSizes] = useState([]);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const material = params.get("material") || "";
  const size = params.get("size") || "";
  const sort = params.get("sort") || "new";
  const minPrice = params.get("min_price") || "";
  const maxPrice = params.get("max_price") || "";
  const page = Number(params.get("page") || 1);
  const pageSize = 12;

  const setParam = (k, v) => {
    const n = new URLSearchParams(params);
    if (!v) n.delete(k); else n.set(k, v);
    if (k !== "page") n.delete("page");
    setParams(n);
  };

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data || []));
    api.get("/materials").then(({ data }) => setMaterials(data || []));
    api.get("/sizes").then(({ data }) => setSizes(data || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const qp = { page, page_size: pageSize, sort };
    if (q) qp.q = q;
    if (category) qp.category = category;
    if (material) qp.material = material;
    if (size) qp.size = size;
    if (minPrice) qp.min_price = minPrice;
    if (maxPrice) qp.max_price = maxPrice;
    api.get("/products", { params: qp }).then(({ data }) => {
      setProducts(data.items || []);
      setTotal(data.total || 0);
    }).finally(() => setLoading(false));
  }, [q, category, material, size, minPrice, maxPrice, sort, page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasFilters = q || category || material || size || minPrice || maxPrice;

  const sizeOptions = (() => {
    if (!category) return sizes.filter((s) => s.group === "all");
    const c = category.toLowerCase();
    const map = { "anéis": "aneis", "aneis": "aneis", "alianças": "aneis", "aliancas": "aneis",
      "colares": "colar", "colar": "colar", "correntes": "colar",
      "pulseiras": "pulseira", "pulseira": "pulseira",
      "brincos": "brinco", "brinco": "brinco" };
    const group = map[c];
    if (!group) return [];
    return sizes.filter((s) => s.group === group);
  })();

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14" data-testid="catalog-page">
      <div className="mb-10">
        <p className="text-[11px] tracking-[0.4em] uppercase text-[#C2A064] mb-3">Catálogo</p>
        <h1 className="font-display text-4xl md:text-5xl text-[#49392F]">
          {category || "Todas as joias"}
        </h1>
        {q && <p className="mt-3 text-[14px] text-[#49392F]/70">Resultados para "<span className="font-medium">{q}</span>"</p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
        {/* Filters */}
        <aside className="space-y-8" data-testid="catalog-filters">
          <div className="flex items-center border border-[#E8DCCB] bg-white px-3">
            <Search size={14} className="text-[#49392F]/60" />
            <input
              value={q}
              onChange={(e) => setParam("q", e.target.value)}
              placeholder="Buscar..."
              className="flex-1 py-2.5 px-3 bg-transparent outline-none text-sm"
              data-testid="catalog-search-input"
            />
          </div>

          <FilterGroup title="Categoria">
            {categories.map((c) => (
              <FilterOption key={c.id} active={category === c.name} onClick={() => setParam("category", category === c.name ? "" : c.name)} label={c.name} />
            ))}
          </FilterGroup>

          <FilterGroup title="Material">
            {materials.map((m) => (
              <FilterOption key={m.id || m.name || m} active={material === (m.name || m)} onClick={() => setParam("material", material === (m.name || m) ? "" : (m.name || m))} label={m.name || m} />
            ))}
            {materials.length === 0 && <div className="text-[12px] text-[#49392F]/50">Nenhum material cadastrado</div>}
          </FilterGroup>

          <FilterGroup title="Tamanho">
            <div className="flex flex-wrap gap-2">
              {sizeOptions.map((s) => (
                <button
                  key={s.id || s.label}
                  onClick={() => setParam("size", size === s.label ? "" : s.label)}
                  className={`min-w-[36px] h-9 px-2 text-[12px] border transition-colors ${size === s.label ? "bg-[#49392F] text-white border-[#49392F]" : "bg-white border-[#E8DCCB] text-[#49392F] hover:border-[#49392F]"}`}
                  data-testid={`filter-size-${s.label}`}
                >
                  {s.label}
                </button>
              ))}
              {sizeOptions.length === 0 && <div className="text-[12px] text-[#49392F]/50">Nenhum tamanho cadastrado</div>}
            </div>
          </FilterGroup>

          <FilterGroup title="Faixa de preço">
            <div className="flex gap-2">
              <input type="number" min="0" value={minPrice} onChange={(e) => setParam("min_price", e.target.value)} placeholder="Mín" className="duo-input" data-testid="filter-min-price" />
              <input type="number" min="0" value={maxPrice} onChange={(e) => setParam("max_price", e.target.value)} placeholder="Máx" className="duo-input" data-testid="filter-max-price" />
            </div>
          </FilterGroup>

          {hasFilters && (
            <button
              onClick={() => setParams({})}
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#49392F] link-elegant"
              data-testid="clear-filters"
            >
              <X size={14} /> Limpar filtros
            </button>
          )}
        </aside>

        {/* Grid */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <span className="text-[12px] text-[#49392F]/70" data-testid="results-count">{total} resultado{total === 1 ? "" : "s"}</span>
            <select
              value={sort}
              onChange={(e) => setParam("sort", e.target.value)}
              className="duo-input max-w-[220px]"
              data-testid="sort-select"
            >
              <option value="new">Novidades</option>
              <option value="price_asc">Menor preço</option>
              <option value="price_desc">Maior preço</option>
            </select>
          </div>

          {loading ? (
            <div className="text-[#49392F]/70">Carregando...</div>
          ) : products.length === 0 ? (
            <div className="py-20 text-center text-[#49392F]/70" data-testid="empty-results">
              <p className="font-display text-2xl text-[#49392F] mb-2">Nenhum produto encontrado</p>
              <p className="text-sm">Tente ajustar os filtros ou limpar a busca.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
                {products.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setParam("page", String(i + 1))}
                      className={`w-9 h-9 text-[12px] border ${page === i + 1 ? "bg-[#49392F] text-white border-[#49392F]" : "bg-white border-[#E8DCCB] text-[#49392F]"}`}
                      data-testid={`page-${i+1}`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function FilterGroup({ title, children }) {
  return (
    <div>
      <h4 className="text-[11px] tracking-[0.3em] uppercase text-[#49392F] mb-4 pb-3 border-b border-[#C2A064]/20">{title}</h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function FilterOption({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`block w-full text-left py-1.5 text-[13px] transition-colors ${active ? "text-[#C2A064] font-medium" : "text-[#49392F] hover:text-[#C2A064]"}`}
    >
      {label}
    </button>
  );
}
